
<template>
  <div class="comment" @mouseover="hoverComment = hashid" @mouseleave="hoverComment = null">
    <avatar class="avatar" :user="avatarUser" size="sm" />
    <div class="comment-content">
      <template v-if="editing">
        <div class="edit-comment-header">
          <p class="edit-caption">
            Editing comment
            <i class="fas fa-pencil-alt"></i>
          </p>
          <a id="cancel-edit" @click="confirmCancel" data-testid="cancel-edit">{{commentChanged ? "Cancel & discard changes" : "Cancel"}}</a>
        </div>
        <div id="edit-comment">
          <vue-tribute :options="tributeOptions">
            <ResizeAuto>
              <template v-slot:default="{resize}">
                <textarea rows="1" id="update-comment-editor" class="textarea vue-simplemde-textarea" v-model="localContent"
                  @tribute-replaced="tributeReplaced"
                  @keydown.stop
                  @keydown.enter.exact.prevent
                  @keyup.enter.exact="updateComment"
                  @input="(e) => onInput(e, resize)"
                  ref="updateComment"
                ></textarea>
              </template>
            </ResizeAuto>
          </vue-tribute>

          <button id="update-comment" name="commit" value="Post" @click="updateComment" data-disable-with="Post" data-testid="post-comment" class="editor-submit submitComment">
            <i class="fas fa-paper-plane" :class="{ 'active': content.length > 0 }"></i>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="comment-header">
          <div class="author">
            <avatar class="mobile-avatar" :user="avatarUser" size="xxs" />
            {{ anonymous ? 'Anonymous' : author }}
          </div>
          <div :class="hoverComment == hashid ? 'd-none' : 'timestamp'" :data-created-at="created_at" :data-updated-at="updated_at" v-if="editable">
            {{ created_at_s }}
            </div>
          <div class="timestamp" :data-created-at="created_at" :data-updated-at="updated_at" v-else>
              {{ created_at_s }}
          </div>
        </div>

        <div class="comment-body">
          <div class="comment-text" v-html="comment_html" v-linkified>
          </div>
          <span class="anonymous-name" v-if="anonymous">
            posted as {{ author }}
          </span>
        </div>

        <unfurled-link v-for="link in cloudappLinks" :link="link" v-bind:key="link" >
        </unfurled-link>
      </template>
    </div>
    <div class="hover-actions" :class="{ active: hoverComment == hashid && !editing }" v-if="editable">
      <a v-if="commentable" href="javascript:;" v-on:click="editing = true" v-b-tooltip.hover title="Edit comment" data-testid="edit-comment"><i class="fas fa-pencil-alt"></i></a>
      <a id="delete-comment" href="javascript:;" v-on:click="destroyComment()" v-b-tooltip.hover title="Delete comment" data-testid="delete-comment"><i class="fas fa-times-circle"></i></a>
    </div>
  </div>
</template>

<script>
import UnfurledLink from "./unfurled_link.vue"
import Avatar from "@cmp/shared/avatar";
import ResizeAuto from '../resize_auto';
import VueTribute from 'vue-tribute';

export default {
  props: {
    anonymous: Boolean,
    content: String,
    hashid: String,
    author: String,
    author_avatar: String,
    created_at: Number,
    updated_at: Number,
    created_at_s: String,
    updated_at_s: String,
    comment_html: String,
    editable: Boolean,
    commentable: Boolean,
    cloudappLinks: Array,
    tributeOptions: Object
  },
  data() {
    return {
      editing: false,
      localContent: this.content,
      disableUpdateOnEnter: false,
      mentions: [],
      hoverComment: false,
    }
  },
  components: { UnfurledLink, Avatar, ResizeAuto, VueTribute },
  computed: {
    avatarUser: function(){
      return { attributes: { name: this.author }, links: { avatar_url: this.author_avatar } };
    },
    commentChanged: function(){
      return this.content != this.localContent;
    }
  },
  methods: {
    onInput: function(e, resizeHandler){
      if(resizeHandler){
        resizeHandler(e);
      }
    },
    destroyComment: function () {
      const vm = this;
      vm.$bvModal.msgBoxConfirm('This cannot be undone.', {
          title: 'Delete this comment?',
          okTitle: 'Delete',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          modalClass: 'confirmation-modal',
          size: 'sm',
          buttonSize: 'sm',
          hideHeaderClose: true,
          centered: true
        })
        .then(confirm => {
          if(confirm){
            vm.$store.dispatch('destroyComment', vm.hashid)
          }
        })
      ;
    },
    tributeReplaced: function(event) {
      this.disableUpdateOnEnter = true;
      this.mentions.push(event["detail"]["item"]["original"]);
    },
    updateComment: function(){
      const vm = this;

      // this prevents selecting a searched user in the mentions box to update the comment. User may not be finished yet.
      if(vm.disableUpdateOnEnter) {
        vm.disableUpdateOnEnter = false;
        return;
      }

      vm.$store.dispatch('updateComment', { commentId: vm.hashid, params: { content: vm.localContent, mentions: vm.mentions } }).then(()=> {
        vm.editing = false
      });
    },
    confirmCancel: function() {
      const vm = this;
      if(!vm.commentChanged) {
        vm.editing = false;
        return;
      }

      vm.$bvModal.msgBoxConfirm('Any edits will be lost.', {
          title: 'Cancel & discard changes?',
          okTitle: 'Confirm',
          cancelTitle: 'Continue editing',
          modalClass: 'confirmation-modal',
          size: 'sm',
          buttonSize: 'sm',
          hideHeaderClose: true,
          centered: true
        })
        .then(confirm => {
          if(confirm){
            vm.editing = false;
            vm.localContent = vm.content;
          }
        })
    },
  },
}
</script>

<style scoped lang="scss">
@import '../../../packs/application.scss';

.comment {
  position: relative;
  margin-bottom: 1rem;
  border-radius: 0.3rem;
  display: flex;

  .d-none {
    display: none;
  }

  .hover-actions {
    background: #ffffff;
    padding-top: 0.6rem;
    padding-right: 1rem;
    width: 5rem;
    text-align: right;
    display: none;

    .fa-trash-alt {
      color: red;
    }
  }

  .active {
    display: block;

    a {
      &:link {
        color: $dark-gray-sf;
      }
      &:hover {
        color: $bright-blue;
      }
      &:active {
        color: $bright-blue;
      }
      &:visited {
        color: $dark-gray-sf;
      }
    }
  }

  .avatar {
    margin-top: .5rem;
    margin-right: 1rem;
  }

  .mobile-avatar {
    display: none;
    margin-right: .5rem;
  }

  .comment-content {
    flex-grow: 2;
    background-color: #ffffff;
    padding: 1rem;
    border-radius: 4px;
  }

  .edit-comment-header {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;

    .edit-caption {
      font-weight: bold;
    }

    #cancel-edit {
      color: $slate-gray;
      font-weight: 600;
      cursor: pointer;
    }
  }

  .comment-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: .5rem;

    .author {
      display: flex;
      align-items: center;
      height: 1rem;
      font-family: SFPro;
      font-size: 0.75rem;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
      color: #4a4a4a;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      &.recent::before {
        content: "\2022";
        color: $bright-blue;
        font-weight: bold;
      }
    }

    .timestamp {
      height: 1rem;
      font-family: SFPro;
      font-size: 0.75rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
      text-align: right;
      white-space: nowrap;
      color: rgba(74, 74, 74, 0.7);
    }
  }

  .comment-body {
    font-family: SFPro;
    font-size: 0.75rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    // color: rgba(44, 44, 46, 0.75);
    color: #4a4a4a;
    word-wrap: break-word;

    p {
      line-height: 1rem;
      font-size: 0.75rem;
    }

    .anonymous-name {
      font-size: .6rem;
      font-weight: bolder;
      color: rgba(74, 74, 74, 0.7);
    }

    .comment-text::v-deep p {
      margin-bottom: 0;
    }
  }

  #edit-comment {
    position: relative;

    #update-comment-editor {
      overflow: auto;
      resize: none;
      width: 100%;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
      border: solid 1px #b0b0b0;
      background-color: var(--c-white);
      padding: 0.5rem 2rem 0.5rem 0.5rem;
      max-height: 10rem;
      font-size: 0.75rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;

      &:focus {
        outline: 0 none;
        -webkit-appearance: none;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
        border: solid 1px #b0b0b0;
      }
    }

    #update-comment {
      border: none;
      position: absolute;
      top: 7px;
      right: 4px;
      background: transparent;
      color: $bright-blue;

    }
  }

  a#delete-comment:hover {
    color: $sunset-orange;
  }
}

@media (max-width: 767px) {
  .avatar {
    display: none;
  }

  .comment {
    margin-bottom: 1px;

    .mobile-avatar {
      display: block;
    }

    .comment-content{
      border-radius: 0;
    }
  }

}
</style>